/* eslint-disable max-lines */
// YC (8/6/25): Disabling max-lines for now to avoid refactoring right now.
import { useMirageAnalyticsContext } from "@mirage/analytics/AnalyticsProvider";
import { PAP_Click_PreviousSession } from "@mirage/analytics/events/types/click_previous_session";
import { PAP_Send_ComposePrompt } from "@mirage/analytics/events/types/send_compose_prompt";
import { initializeWorkflowAgentConversation } from "@mirage/mosaics/Chat/components/chat/instructions/initializeWorkflowAgentConversation";
import type { ChatSource } from "@mirage/mosaics/Chat/types";
import { useGetConversationAttachments } from "@mirage/mosaics/ConversationAssistant/hooks/attachments/useConversationAttachments";
import type { SelectedTemplate } from "@mirage/mosaics/ConversationAssistant/hooks/useConversationDraftConfig";
import type { ComposeParams } from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/assist_compose_pb";
import {
  DashSearch,
  NoSearch,
  SearchParams,
} from "@mirage/service-dbx-api/service/grpc/context_engine_apiv2/gen/context_engine_conversations_service_pb";
import { useFeatureFlagValueBool } from "@mirage/service-experimentation/useFeatureFlagValue";
import { tagged } from "@mirage/service-logging";
import type { PreConfiguredTemplateID } from "@mirage/shared/compose/assist-template";
import {
  getTemplateDisplayName,
  templateIdToTemplateName,
} from "@mirage/shared/compose/assist-template";
import { getPreConfiguredVoiceIdToDraftName } from "@mirage/shared/compose/compose-voice";
import i18n from "@mirage/translations";
import type { SetStateAction } from "react";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  appendManualMessages,
  postUserMessage,
  updateConversationDraft,
} from "..";
import {
  type ConversationRouterState,
  ConversationType,
  CtaSource,
} from "../router_state_types";
import type {
  ConversationClientContext,
  NewUserMessage,
  UserMessageOptions,
} from "../types";
import { convertChatSourceToDocumentInfo } from "../utils/convertChatSourceToDocumentInfo";
import { convertSuggestionSourceToDocumentInfo } from "../utils/convertSuggestionQueryToDocumentInfo";
import {
  createConversationClientContext,
  createFssClientContextWithSources,
} from "../utils/createConversationClientContext";
import {
  getDashActionSurfaceComponentLoggingSource,
  getDashActionSurfaceComponentLoggingSourceParams,
  getPapEventPropsForSummarizeItem,
} from "../utils/loggingUtils";

const logger = tagged("conversation-router-state");
/**
 * Cache to track processed states based on location.key
 * which is unique to every navigation event. This cache
 * ensures that we never process router state more than once.
 */
const processedStates = new Set<string>();

const searchParamsNoSearch = new SearchParams({
  searchTarget: {
    value: new NoSearch(),
    case: "noSearch",
  },
});

export interface UseConversationRouterStateProps {
  agentPayload?: ComposeParams;
  chatSessionId?: string;
  conversationId?: string;
  setSelectedVoiceID: (voiceID: string) => void;
  setSelectedTemplate: (template: SelectedTemplate) => void;
  setUserMessageOptions: (options: SetStateAction<UserMessageOptions>) => void;
  setManuallyAddedSources: (sources: SetStateAction<ChatSource[]>) => void;
  setActionSurface: (surface?: string) => void;
  storeInitialState: (state: {
    userMessageOptions?: UserMessageOptions; // includes clientContext, searchParams, etc.
  }) => void;
}

const isConversationRouterState = (
  state: unknown,
): state is ConversationRouterState => {
  return (
    state != null && typeof state === "object" && "conversationType" in state
  );
};

const convertAttachments = (sources: ChatSource[]) => {
  return sources.map(convertChatSourceToDocumentInfo);
};

export const useConversationRouterState = ({
  agentPayload,
  chatSessionId,
  conversationId,
  setSelectedVoiceID,
  setSelectedTemplate,
  setUserMessageOptions,
  setManuallyAddedSources,
  setActionSurface,
  storeInitialState,
}: UseConversationRouterStateProps) => {
  const navigate = useNavigate();
  const { state: routerState, key: locationKey, pathname } = useLocation();
  const { reportPapEvent } = useMirageAnalyticsContext();
  const isScopedStackChatEnabled = useFeatureFlagValueBool(
    "dash_web_2025_08_08_stack_chat_namespace_id",
  );

  const chatSources =
    routerState && "chatSources" in routerState ? routerState.chatSources : [];
  const getAttachments = useGetConversationAttachments(
    [],
    chatSources,
    undefined,
    [],
    undefined,
  );

  const preConfiguredVoiceIdToDraftName = getPreConfiguredVoiceIdToDraftName();

  const setUp = useCallback(async () => {
    if (
      !routerState ||
      !conversationId ||
      !chatSessionId ||
      routerState.processed ||
      !isConversationRouterState(routerState)
    ) {
      return;
    }

    // Skip if already processed
    if (processedStates.has(locationKey)) {
      logger.debug(`Already processed state: ${locationKey}`);
      return;
    }

    // Mark as processed
    processedStates.add(locationKey);

    const { dashActionSurface, actionSurfaceComponent } =
      getDashActionSurfaceComponentLoggingSourceParams(routerState.ctaSource);
    const source = getDashActionSurfaceComponentLoggingSource(
      routerState.ctaSource,
    );
    let clientContext: ConversationClientContext | undefined;

    // Reset action surface to undefined to avoid any stale values, particularly for
    // non-FSS cases.
    setActionSurface(undefined);

    switch (routerState.conversationType) {
      case ConversationType.SummarizeItem:
        {
          const papEventProps = getPapEventPropsForSummarizeItem(
            routerState as ConversationRouterState & {
              chatSources: ChatSource[];
            },
            chatSessionId,
            actionSurfaceComponent,
            dashActionSurface,
            routerState.surface,
          );

          reportPapEvent(PAP_Send_ComposePrompt(papEventProps));

          let clientContext: ConversationClientContext | undefined;
          let stackId = undefined;
          if (routerState.scenarioMeta?.type == "stack") {
            clientContext = createConversationClientContext("stack", {
              clientScenarioId: routerState.scenarioMeta?.sharingId,
            });

            if (isScopedStackChatEnabled) {
              stackId = routerState.scenarioMeta?.namespaceId;
            }
          }

          postUserMessage({
            agentIds: ["summarize"],
            conversationId,
            message:
              routerState.ctaSource === CtaSource.StackSummarize
                ? i18n.t("summarize_this_stack")
                : i18n.t("summarize_documents", {
                    count: routerState.chatSources.length,
                  }),
            attachments: await getAttachments(),
            clientContext,
            source,
            stackId,
          });

          // Disable search by default to keep follow-ups scoped
          setUserMessageOptions({
            searchParams: new SearchParams({
              searchTarget: {
                value: new NoSearch(),
                case: "noSearch",
              },
            }),
            stackId,
          });

          // Set the action surface to the surface of the router state
          setActionSurface(routerState.surface);
        }
        break;
      case ConversationType.Ask:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentPayload: JSON.stringify(agentPayload),
          source,
        });
        break;
      case ConversationType.AskFromSuggestion:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_typeahead",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentPayload: JSON.stringify(agentPayload),
          attachments: routerState.attachments.map(
            convertSuggestionSourceToDocumentInfo,
          ),
          clientContext: createConversationClientContext("suggested_query"),
          agentIds: routerState.agentIds,
          source,
        });
        setUserMessageOptions({
          agentIds: routerState.agentIds,
        });
        break;
      case ConversationType.WriteWithMessage:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          conversationId,
          message: routerState.message,
          agentIds: routerState.agentIds,
          agentPayload: JSON.stringify(agentPayload),
          source,
        });
        break;
      case ConversationType.WriteWithDocument:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_typeahead",
          }),
        );

        appendManualMessages([
          {
            conversationId,
            message: "",
            agentIds: routerState.agentIds,
            attachments: await getAttachments(),
            messageType: "userMessage",
            source,
          },
          {
            conversationId,
            message: i18n.t("compose_assistant_message_partial_match"),
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          searchParams: searchParamsNoSearch,
          agentIds: ["write"],
        });
        break;
      case ConversationType.WriteFromTemplate:
        {
          const id =
            routerState.templateId as unknown as PreConfiguredTemplateID;
          const name = getTemplateDisplayName(templateIdToTemplateName[id]);

          reportPapEvent(
            PAP_Send_ComposePrompt({
              actionSurfaceComponent,
              chatSessionId,
              dashActionSurface,
              featureLine: "assist",
              promptType:
                routerState.ctaSource === CtaSource.OmniboxDraftWithTemplate
                  ? "omnibox_typeahead"
                  : "assistant_first_prompt",
            }),
          );

          setSelectedTemplate({
            id,
            name,
          });
          setUserMessageOptions({
            searchParams: searchParamsNoSearch,
            agentIds: ["write"],
          });
        }
        break;
      case ConversationType.WriteFromTone:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_typeahead",
          }),
        );

        setSelectedVoiceID(routerState.voiceId);
        appendManualMessages([
          {
            conversationId,
            message:
              preConfiguredVoiceIdToDraftName[
                routerState.voiceId as keyof typeof preConfiguredVoiceIdToDraftName
              ],
            agentIds: routerState.agentIds,
            agentPayload: JSON.stringify(agentPayload),
            messageType: "userMessage",
            source,
          },
          {
            conversationId,
            message: i18n.t("compose_assistant_message_partial_match"),
            agentIds: routerState.agentIds,
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          agentIds: ["write"],
        });
        break;
      case ConversationType.AskWithMultiDocs:
        // NOTE: We explicitly _do not_ log send.compose_prompt here, because this
        // case does not involve a pre-populated user message. This was an ask from DS.
        appendManualMessages([
          {
            attachments: await getAttachments(),
            clientContext:
              routerState.scenarioMeta.type === "stack"
                ? createConversationClientContext("stack", {
                    clientScenarioId: routerState.scenarioMeta.sharingId,
                  })
                : undefined,
            conversationId,
            message: "",
            messageType: "userMessage",
            source,
            stackId:
              isScopedStackChatEnabled &&
              routerState.scenarioMeta.type === "stack"
                ? routerState.scenarioMeta.namespaceId
                : undefined,
          },
          {
            conversationId,
            message: i18n.t("stack_chat_assistant_prompt"),
            messageType: "assistantMessage",
            source,
          },
        ]);
        setUserMessageOptions({
          searchParams: searchParamsNoSearch,
          stackId:
            isScopedStackChatEnabled &&
            routerState.scenarioMeta.type === "stack"
              ? routerState.scenarioMeta.namespaceId
              : undefined,
        });
        break;
      case ConversationType.AskWithPromptMultiDocs:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "chat_suggestion",
            queryString: routerState.message,
            queryLength: routerState.message.length,
          }),
        );

        postUserMessage({
          attachments: await getAttachments(),
          clientContext:
            routerState.scenarioMeta.type === "stack"
              ? createConversationClientContext("stack", {
                  clientScenarioId: routerState.scenarioMeta.sharingId,
                })
              : undefined,
          conversationId,
          message: routerState.message,
          source,
          searchParams: searchParamsNoSearch,
          stackId:
            isScopedStackChatEnabled &&
            routerState.scenarioMeta.type === "stack"
              ? routerState.scenarioMeta.namespaceId
              : undefined,
        });
        setUserMessageOptions({
          searchParams: searchParamsNoSearch,
          stackId:
            isScopedStackChatEnabled &&
            routerState.scenarioMeta.type === "stack"
              ? routerState.scenarioMeta.namespaceId
              : undefined,
        });
        break;
      case ConversationType.WorkflowAgent:
        if (routerState.scenarioMeta.type === "workflow_agent") {
          initializeWorkflowAgentConversation(
            routerState.scenarioMeta.workflowAgentId,
            conversationId,
            source,
            setUserMessageOptions,
          );
        }
        break;
      case ConversationType.LegacySession: {
        const messagesToAppend = routerState.messageHistory
          .map<NewUserMessage>((message) => ({
            conversationId,
            message: message.content,
            attachments:
              message.attachments && message.attachments.length > 0
                ? convertAttachments(message.attachments)
                : undefined,
            messageType:
              message.role === "user" ? "userMessage" : "assistantMessage",
            source,
          }))
          .filter((message) => message.message.trim().length > 0); // Filter out messages with empty content

        if (messagesToAppend.length > 0) {
          appendManualMessages(messagesToAppend);
        }

        // Set markdown content if available
        if (routerState.markdownContent) {
          updateConversationDraft(conversationId, routerState.markdownContent);
        }
        break;
      }
      case ConversationType.RecentChat:
        // This is a bit funky, but it allows us to record actions that return the user to a previous chat
        // session with the appropriate chatSessionId that's generated only after the user lands on the
        // conversation page. Because no other action is taken on the user's behalf, this is a no-op outside
        // of the log.
        reportPapEvent(
          PAP_Click_PreviousSession({
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
          }),
        );

        break;
      case ConversationType.FssAsk:
        {
          const userMessageOptions = {
            clientContext: createConversationClientContext("fss-unscoped"),
            searchParams: new SearchParams({
              searchTarget: {
                value: new DashSearch(),
                case: "dashSearch",
              },
            }),
          };
          setUserMessageOptions(userMessageOptions);
          setActionSurface(routerState.surface);
          storeInitialState({
            userMessageOptions,
          });
        }
        break;
      case ConversationType.FssAskQuery:
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurface: routerState.surface,
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType: "omnibox_query",
            queryString: routerState.message,
            queryLength: routerState.message.length,
            searchSessionId:
              routerState.scenarioMeta.type === "fss_serp_query"
                ? routerState.scenarioMeta.searchSessionId
                : undefined,
          }),
        );
        postUserMessage({
          conversationId,
          message: routerState.message,
          clientContext: createConversationClientContext("fss-serp-answer"),
          agentIds: ["conversations"],
          source,
        });
        setUserMessageOptions({
          // This changes if the user engages further with the conversation
          clientContext: createConversationClientContext("fss-serp-follow-up"),
          searchParams: new SearchParams({
            searchTarget: {
              value: new DashSearch(),
              case: "dashSearch",
            },
          }),
        });
        setActionSurface(routerState.surface);
        storeInitialState({});
        break;
      case ConversationType.FssAskFromSuggestion: {
        reportPapEvent(
          PAP_Send_ComposePrompt({
            actionSurface: routerState.surface,
            actionSurfaceComponent,
            chatSessionId,
            dashActionSurface,
            featureLine: "assist",
            promptType:
              routerState.ctaSource === CtaSource.FssOneClickAskPrompt
                ? "chat_suggestion"
                : "omnibox_typeahead",
            queryString: routerState.message,
            queryLength: routerState.message.length,
            searchSessionId:
              routerState.scenarioMeta.type === "fss_serp_query"
                ? routerState.scenarioMeta.searchSessionId
                : undefined,
          }),
        );

        // We only want to set clientContext and update search params when this will be
        // the first interaction with the conversation. The OneClickAskPrompt case is
        // different because it's triggered from within the chat itself, and thus requires
        // a conversation scope to already exist, one that we usually would want to maintain
        // after this kickoff interaction.

        clientContext =
          routerState.ctaSource === CtaSource.FssAskFromSuggestion
            ? createConversationClientContext("fss-suggested-query")
            : undefined;
        const attachments = routerState.attachments.map(
          convertSuggestionSourceToDocumentInfo,
        );
        postUserMessage({
          conversationId,
          message: routerState.message,
          agentPayload: JSON.stringify(agentPayload),
          attachments,
          clientContext: clientContext,
          agentIds: routerState.agentIds,
          source,
        });

        if (routerState.ctaSource === CtaSource.FssAskFromSuggestion) {
          setUserMessageOptions({
            searchParams: new SearchParams({
              searchTarget: {
                value: new DashSearch(),
                case: "dashSearch",
              },
            }),
          });
          storeInitialState({});
        }
        setActionSurface(routerState.surface);
        break;
      }
      case ConversationType.FssAskWithMultiDocs: {
        // NOTE: We explicitly _do not_ log send.compose_prompt here, because this
        // case does not involve a pre-populated user message. This was an ask from DS.
        // This state replicates the Add Sources action only
        setManuallyAddedSources(routerState.chatSources);
        setUserMessageOptions({
          searchParams: searchParamsNoSearch,
        });
        setActionSurface(routerState.surface);
        break;
      }
      case ConversationType.FssAskWithFile: {
        if (routerState.scenarioMeta.type === "fss_file_preview") {
          clientContext = createFssClientContextWithSources(
            "fss-file-preview",
            routerState.scenarioMeta.file,
          );
        } else if (routerState.scenarioMeta.type === "fss_stack") {
          clientContext = createFssClientContextWithSources(
            "fss-stack",
            routerState.scenarioMeta.files,
          );
        }

        const userMessageOptions = {
          searchParams: searchParamsNoSearch,
          clientContext,
        };
        setUserMessageOptions(userMessageOptions);
        setActionSurface(routerState.surface);
        storeInitialState({
          userMessageOptions,
        });
        break;
      }
      case ConversationType.FssAskWithFolder: {
        if (routerState.scenarioMeta.type === "fss_folder") {
          clientContext = createFssClientContextWithSources(
            "fss-folder",
            routerState.scenarioMeta.folder,
          );

          const userMessageOptions = {
            searchParams: new SearchParams({
              searchTarget: {
                value: new DashSearch(),
                case: "dashSearch",
              },
            }),
            fssSearchScope: routerState.scenarioMeta.folder.path,
            clientContext,
          };
          setUserMessageOptions(userMessageOptions);
          setActionSurface(routerState.surface);
          storeInitialState({
            userMessageOptions,
          });
        }
        break;
      }
      default:
        logger.error(
          `No matching handler for router state type: ${(routerState as ConversationRouterState).conversationType}`,
        );
        break;
    }

    // Keep the router state intact for components that require it, but mark the state as processed
    // to avoid reprocessing on refreshes.
    navigate(pathname, {
      replace: true,
      state: {
        ...routerState,
        processed: true,
      },
    });
  }, [
    routerState,
    conversationId,
    chatSessionId,
    locationKey,
    navigate,
    pathname,
    reportPapEvent,
    agentPayload,
    setUserMessageOptions,
    getAttachments,
    setSelectedVoiceID,
    preConfiguredVoiceIdToDraftName,
    setSelectedTemplate,
    setManuallyAddedSources,
    setActionSurface,
    storeInitialState,
  ]);

  useEffect(() => {
    setUp();
  }, [setUp]);
};
